import { useEffect, useState } from 'react'
import './App.css'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router'
import Loading from './components/auth/loading'
import FeatureActionListPage from './pages/FeatureActionListPage'
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import MainLayout from './layouts/MainLayout';
import SiteLayout from './layouts/site-layout';
import SiteCourseFeatureTypeList from './pages/SiteCourseFeatureTypeList';
import HabitatTaskList from './pages/SiteHabitatTypeListPage';
import SiteProjectListPage from './pages/SiteProjectListPage';
import ProjectEditPage from './pages/project-edit-page';
import ProjectCreatePage from './pages/ProjectCreatePage';
import AreaListPage from './pages/AreaListPage';
import { actionEditUrlBuilder, areaCourseFeatureUrlBuilder, areaCourseObservationsUrlBuilder, areaHabitatFeatureUrlBuilder, areaHabitatObservationsUrlBuilder, areaIndexUrlBuilder, areaTasksUrlBuilder, areaUrlBuilder, courseUrlBuilder, habitatUrlBuilder, projectEditUrlBuilder, projectTasksUrlBuilder, projectUrlBuilder, siteUrlBuilder, siteActionsUrlBuilder, siteMapUrlBuilder, featureTasksUrlBuilder, featureUrlBuilder, featureTypeUrlBuilder, featureTypeFeatureListUrlBuilder, featureTypeActionListUrlBuilder, featureObservationsUrlBuilder, observationUrlBuilder, featureTypeObservationListUrlBuilder, siteObservationsUrlBuilder, areaObservationsUrlBuilder } from './lib/url-builder';
import ProjectActionsPage from './pages/ProjectActionsPage';
import AreaCourseFeatureListPage from './pages/AreaCourseFeatureListPage';
import AreaHabitatFeatureListPage from './pages/AreaHabitatFeatureListPage';
import AreaActionListPage from './pages/AreaActionListPage';
import AreaCourseObservationListPage from './pages/AreaCourseObservationListPage';
import AreaHabitatObservationListPage from './pages/AreaHabitatObservationListPage';
import { CacheService } from './services/CacheService';
import { appConfig } from './lib/AppConfig';
import { SiteService } from './services/SiteService';
import { useSiteName } from './hooks/useSiteName';
import AreaEditPage from './pages/AreaEditPage';
import ActionEditPage from './pages/ActionEditPage';
import SiteActionListPage from './pages/SiteActionListPage';
import ActionCreatePage from './pages/ActionCreatePage';
import AreaCreatePage from './pages/AreaCreatePage';
import SiteIndexPage from './pages/SiteIndexPage';
import FeatureEditPage from './pages/FeatureEditPage';
import FeatureTypeCreatePage from './pages/FeatureTypeCreatePage';
import FeatureTypeEditPage from './pages/FeatureTypeEditPage';
import FeatureTypeFeatureListPage from './pages/FeatureTypeFeatureListPage';
import FeatureTypeActionListPage from './pages/FeatureTypeActionListPage';
import { SiteListPage } from './pages/SiteListPage';
import { MapFeaturePage } from './pages/MapFeaturePage';
import FeatureObservationListPage from './pages/FeatureObservationListPage';
import ObservationCreatePage from './pages/ObservationCreatePage';
import ObservationEditPage from './pages/ObservationEditPage';
import FeatureTypeObservationListPage from './pages/FeatureTypeObservationListPage';
import { ToastContainer } from 'react-toastify';
import SiteObservationListPage from './pages/SiteObservationListPage';
import * as Sentry from '@sentry/react';
import SiteAdminPage from './pages/SiteAdminPage';
import AreaObservationListPage from './pages/AreaObservationListPage';
import UserProfilePage from './pages/UserProfilePage';
import { AuthenticateWithRedirectCallback, useAuth, useUser } from '@clerk/react'

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth()

    if (!isLoaded) {
      return <Loading />  // Your loading component
    }

    if (!isSignedIn) {
      return <Navigate to="/signin" replace />
    }

    return <>{children}</>
}

function TopNavItems() {
  const navItems = [
    { to: "/", label: 'Sites', end: true },
  ];
  return <MainLayout navItems={navItems} siteName="" levelName="" />;
}

function MainNavItems() {
  const { siteId, tenantId } = useParams();
  const { siteName, error, } = useSiteName(siteId);

  useEffect(() => {
    if (siteName) {
      document.title = siteName;
    }
  }, [error, siteName]);

  const navItems = [
    { to: siteMapUrlBuilder(tenantId!, siteId!, null), label: 'Map', end: true },
    { to: courseUrlBuilder(tenantId!,siteId!), label: 'Course', end: true },
    { to: habitatUrlBuilder(tenantId!, siteId!), label: 'Habitat', end: false },
    { to: siteObservationsUrlBuilder(tenantId!, siteId!), label: 'Observations', end: true },
    { to: areaUrlBuilder(tenantId!, siteId!), label: 'Areas', end: false },
    { to: projectUrlBuilder(tenantId!, siteId!), label: 'Projects', end: false },
    { to: siteActionsUrlBuilder(tenantId!, siteId!), label: 'Tasks', end: false },
    { to: siteUrlBuilder(tenantId!, siteId!), label: 'Details', end: true },
  ];

  return <MainLayout navItems={navItems} siteName={siteName ?? ""} levelName="" />;
}

function FeatureNavItems() {
  const { tenantId, siteId, featureId } = useParams();
  const { siteName } = useSiteName(siteId);

  // useEffect(() => {
  //   alert(siteName  ?? "Loading...");
  // }, [isLoading, siteName]);

  const navItems = [
    { to: featureUrlBuilder(tenantId!, siteId!, featureId!), label: 'Details', end: true },
    { to: featureObservationsUrlBuilder(tenantId!, siteId!, featureId!), label: 'Observations', end: true },
    { to: featureTasksUrlBuilder(tenantId!, siteId!, featureId!), label: 'Tasks', end: true },
  ];

  return <MainLayout navItems={navItems} siteName={siteName ?? ""} levelName="" />;
}

function ObservationNavItems() {
  const { tenantId, siteId, observationId } = useParams();
  const { siteName } = useSiteName(siteId);

  // useEffect(() => {
  //   alert(siteName  ?? "Loading...");
  // }, [isLoading, siteName]);

  const navItems = [
    { to: observationUrlBuilder(tenantId!, siteId!, observationId!), label: 'Details', end: true },
  ];

  return <MainLayout navItems={navItems} siteName={siteName ?? ""} levelName="" />;
}

function FeatureTypeNavItems() {
  const { tenantId, siteId, featureTypeId } = useParams();
  const { siteName } = useSiteName(siteId);

  // useEffect(() => {
  //   alert(siteName  ?? "Loading...");
  // }, [isLoading, siteName]);

  const navItems = [
    { to: featureTypeFeatureListUrlBuilder(tenantId!, siteId!, featureTypeId!), label: 'Features', end: true },
    { to: featureTypeActionListUrlBuilder(tenantId!, siteId!, featureTypeId!), label: 'Tasks', end: true },
    { to: featureTypeObservationListUrlBuilder(tenantId!, siteId!, featureTypeId!), label: 'Observations', end: true },
    { to: featureTypeUrlBuilder(tenantId!, siteId!, featureTypeId!), label: 'Details (todo)', end: true },
  ];

  return <MainLayout navItems={navItems} siteName={siteName ?? ""} levelName="" />;
}

function ProjectNavItems() {
  const { tenantId, siteId, projectId } = useParams();
  const { siteName, } = useSiteName(siteId);

  const navItems = [
    { to: projectEditUrlBuilder(tenantId!, siteId!, projectId!), label: 'Details', end: true },
    { to: projectTasksUrlBuilder(tenantId!, siteId!, projectId!), label: 'Tasks', end: true },
  ];
  
  return <MainLayout navItems={navItems} siteName={siteName ?? ""} levelName="" />;
}

function AreaNavItems() {
  const { tenantId, siteId, areaId } = useParams();
  const [siteName, setSiteName] = useState('');

  useEffect(() => {
    const initialiseArea = async () => {
      const cachedArea = await CacheService.get(siteId!, appConfig.resourceTypes.site, async (id) => {
        const response = await SiteService.getSiteById(id);
        return response;
      });

      setSiteName(cachedArea.name);
    }
    initialiseArea()
  }, [siteId]);

  const navItems = [
    { to: areaIndexUrlBuilder(tenantId!, siteId!, areaId!), label: 'Details', end: true },
    { to: areaCourseFeatureUrlBuilder(tenantId!, siteId!, areaId!), label: 'Course Features', end: true },
    { to: areaHabitatFeatureUrlBuilder(tenantId!, siteId!, areaId!), label: 'Habitat Features', end: true },
    { to: areaTasksUrlBuilder(tenantId!, siteId!, areaId!), label: 'Tasks', end: false },
    { to: areaObservationsUrlBuilder(tenantId!, siteId!, areaId!), label: 'Observations', end: true },
    { to: areaCourseObservationsUrlBuilder(tenantId!, siteId!, areaId!), label: 'Course Observations', end: true },
    { to: areaHabitatObservationsUrlBuilder(tenantId!, siteId!, areaId!), label: 'Habitat Observations', end: true },
  ];

  return <MainLayout navItems={navItems} siteName={siteName} levelName="" />;
}

function TaskNavItems() {
  const { tenantId, siteId, actionId } = useParams();
  const [siteName, setSiteName] = useState('');

  useEffect(() => {
    const initialiseArea = async () => {
      const cachedArea = await CacheService.get(siteId!, appConfig.resourceTypes.site, async (id) => {
        const response = await SiteService.getSiteById(id);
        return response;
      });

      setSiteName(cachedArea.name);
    }
    initialiseArea()
  }, [siteId]);

  const navItems = [
    { to: actionEditUrlBuilder(tenantId!, siteId!, actionId!), label: 'Details', end: false },
  ];

  return <MainLayout navItems={navItems} siteName={siteName} levelName="" />;
}

function App() {
  // Example on custom nav https://github.com/costinEEST/msal-react/blob/main/src/NavigationClient.ts
  // const navigate = useNavigate();
  // const navigationClient = new CustomNavigationClient(navigate);
  // pca.setNavigationClient(navigationClient);

const AuthHandler = () => {
  const { isLoaded, isSignedIn, userId } = useAuth()
  const { user, isLoaded: userLoaded } = useUser()
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded || !userLoaded) return

    if (isSignedIn && user) {
      // Set Sentry user (Clerk provides richer data)
      Sentry.setUser({
        id: userId || user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.fullName || user.username || user.emailAddresses[0]?.emailAddress,
      })

      // Optional: Redirect after successful sign-in
      // navigate("/", { replace: true });
    }
  }, [isLoaded, userLoaded, isSignedIn, userId, user, navigate])

  return null
}

  return (
    <>
          <meta name="gitshashort" content={import.meta.env.VITE_GIT_SHORT_SHA} />
          <meta name="gitsha" content={import.meta.env.VITE_GIT_SHA} />
              <AuthHandler />
          <Routes>
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route element={<SiteLayout />}>
              <Route index element={<ProtectedRoute><SiteListPage /></ProtectedRoute>} />
            </Route>

            <Route element={<TopNavItems />}>
              <Route path="profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            </Route>

            <Route path=":tenantId/sites/:siteId">
              <Route element={<MainNavItems  />}>
                <Route index element={<ProtectedRoute><SiteIndexPage /></ProtectedRoute>} />
                <Route path="map" element={<ProtectedRoute><MapFeaturePage /></ProtectedRoute>} />
                <Route path="course" element={<ProtectedRoute><SiteCourseFeatureTypeList /></ProtectedRoute>} />
                <Route path="habitat" element={<ProtectedRoute><HabitatTaskList /></ProtectedRoute>} />
                <Route path="area" element={<ProtectedRoute><AreaListPage /></ProtectedRoute>} />
                <Route path="area/create" element={<ProtectedRoute><AreaCreatePage /></ProtectedRoute>} />
                <Route path="project" element={<ProtectedRoute><SiteProjectListPage /></ProtectedRoute>} />
                <Route path="project/create" element={<ProtectedRoute><ProjectCreatePage /></ProtectedRoute>} />
                <Route path="featuretypes/create" element={<ProtectedRoute><FeatureTypeCreatePage /></ProtectedRoute>} />
                <Route path="observations/create" element={<ProtectedRoute><ObservationCreatePage /></ProtectedRoute>} />
                <Route path="tasks" element={<ProtectedRoute><SiteActionListPage /></ProtectedRoute>} />
                <Route path="observations" element={<ProtectedRoute><SiteObservationListPage /></ProtectedRoute>} />
                <Route path="admin" element={<ProtectedRoute><SiteAdminPage /></ProtectedRoute>} />
              </Route>

              <Route element={<TaskNavItems />}>
                <Route path="tasks/:taskId" element={<ProtectedRoute><ActionEditPage /></ProtectedRoute>} />
                <Route path="tasks/create" element={<ProtectedRoute><ActionCreatePage /></ProtectedRoute>} />
              </Route>

              <Route element={<AreaNavItems />}>
                <Route path="area/:areaId" element={<ProtectedRoute><AreaEditPage /></ProtectedRoute>} />
                <Route path="area/:areaId/coursefeature" element={<ProtectedRoute><AreaCourseFeatureListPage /></ProtectedRoute>} />
                <Route path="area/:areaId/habitatfeature" element={<ProtectedRoute><AreaHabitatFeatureListPage /></ProtectedRoute>} />
                <Route path="area/:areaId/observations" element={<ProtectedRoute><AreaObservationListPage /></ProtectedRoute>} />
                <Route path="area/:areaId/tasks" element={<ProtectedRoute><AreaActionListPage /></ProtectedRoute>} />
                <Route path="area/:areaId/courseobservations" element={<ProtectedRoute><AreaCourseObservationListPage /></ProtectedRoute>} />
                <Route path="area/:areaId/habitatobservations" element={<ProtectedRoute><AreaHabitatObservationListPage /></ProtectedRoute>} />
              </Route>

              <Route element={<ProjectNavItems />}>
                <Route path="project/:projectId" element={<ProtectedRoute><ProjectEditPage /></ProtectedRoute>} />
                <Route path="project/:projectId/tasks" element={<ProtectedRoute><ProjectActionsPage /></ProtectedRoute>} />
              </Route>
              
              <Route path="features">
                <Route element={<FeatureNavItems />}>
                  <Route path=":featureId" element={<ProtectedRoute><FeatureEditPage /></ProtectedRoute>} />
                  <Route path=":featureId/tasks" element={<ProtectedRoute><FeatureActionListPage /></ProtectedRoute>} />
                  <Route path=":featureId/observations" element={<ProtectedRoute><FeatureObservationListPage /></ProtectedRoute>} />
                  {/* <Route path=":featureId/tasks/:taskId" element={<ProtectedRoute><FeatureActionEditPage /></ProtectedRoute>} /> */}
                </Route>
              </Route>

              <Route path="observations">
                <Route element={<ObservationNavItems />}>
                  <Route path=":observationId" element={<ProtectedRoute><ObservationEditPage /></ProtectedRoute>} />
                </Route>
              </Route>              

              <Route element={<FeatureTypeNavItems />}>
                <Route path="featuretypes/:featureTypeId" element={<ProtectedRoute><FeatureTypeEditPage /></ProtectedRoute>} />
                <Route path="featuretypes/:featureTypeId/features" element={<ProtectedRoute><FeatureTypeFeatureListPage /></ProtectedRoute>} />
                <Route path="featuretypes/:featureTypeId/observations" element={<ProtectedRoute><FeatureTypeObservationListPage /></ProtectedRoute>} />
                <Route path="featuretypes/:featureTypeId/tasks" element={<ProtectedRoute><FeatureTypeActionListPage /></ProtectedRoute>} />
              </Route>
            </Route>
          </Routes>
          
          <ToastContainer />
          
        {/* <a href="https://vite.dev" target="_blank">
          <img src={viteLogo} className="logo" alt="Vite logo" />
        </a>
        <a href="https://react.dev" target="_blank">
          <img src={reactLogo} className="logo react" alt="React logo" />
        </a> */}
      
      {/* <h1>Vite + React</h1>
      <div className="card">
        <button onClick={() => setCount((count) => count + 1)}>
          count is {count} {userDisplayName}
        </button>
        <p>
          Edit <code>src/App.tsx</code> and save to test HMR
        </p>
      </div>
      <p className="read-the-docs">
        Click on the Vite and React logos to learn more
      </p> */}
    </>
  )
}

export default App
